import React, { useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { hasAnalyticsAccess, hasReportsAccess } from '../utils/accessControl';
import { useAppStore } from '../stores/useAppStore';
import { Modal } from './ui/Modal';
import {
  ShoppingCart,
  Store,
  RefreshCw,
  Package,
  BarChart3,
  Settings,
  ArrowLeft,
  Calculator,
  Camera,
  Wallet,
  FileSpreadsheet,
  PackageX
} from 'lucide-react';

const NavigationModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { itemCount } = useCart();
  const features = useAppStore((s) => s.features);
  const firstButtonRef = useRef(null);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);

  // Define navigation items with access control
  const getNavigationItems = () => {
    // If user is salesman (ID 4), only show scanner
    if (user?.id === 4) {
      return [
        { label: 'Scanner', path: '/scanner', icon: Camera, description: 'Barcode scanner' }
      ];
    }

    const baseItems = [
      { label: 'POS', path: '/', icon: ShoppingCart, description: 'Point of Sale' },
      { label: 'Scanner', path: '/scanner', icon: Camera, description: 'Barcode scanner' },
      { label: 'Items', path: '/items', icon: Store, description: 'Manage inventory' },
      { label: 'Sales', path: '/sales', icon: Store, description: 'View sales history' },
      { label: 'Sale Returns', path: '/sale-return', icon: Store, description: 'Process returns' },
      { label: 'Exchanges', path: '/exchange', icon: RefreshCw, description: 'Handle exchanges' },
      { label: 'Purchases', path: '/purchases', icon: Package, description: 'Manage purchases' },
      { label: 'Purchase Returns', path: '/purchase-returns', icon: ArrowLeft, description: 'Manage purchase returns' },
      { label: 'Expenses', path: '/expenses', icon: Wallet, description: 'Track daily expenses' },
    ];

    // Add Closing only for Admin and Manager users (user ID 1 or 2, or is_admin)
    if (user?.id === 1 || user?.id === 2 || user?.is_admin) {
      baseItems.push({ 
        label: 'Closing', 
        path: '/closings', 
        icon: Calculator, 
        description: 'Daily closing operations' 
      });
    }

    // Add Analytics only for users with analytics access
    if (hasAnalyticsAccess(user)) {
      baseItems.push({ 
        label: 'Analytics', 
        path: '/analytics2', 
        icon: BarChart3, 
        description: 'View reports and analytics' 
      });
    }

    // Add Reports for admin and manager users (admins build, managers run)
    if (hasReportsAccess(user)) {
      baseItems.push({
        label: 'Reports',
        path: '/reports',
        icon: FileSpreadsheet,
        description: 'Build and run custom reports'
      });
    }

    // Add Business Information only for admin users (user ID 1)
    if (user?.id === 1) {
      baseItems.push({ 
        label: 'Business Info', 
        path: '/business-information', 
        icon: Settings, 
        description: 'Manage business settings' 
      });
    }

    // Last, so adding it does not shift the number/F-key shortcuts staff already use.
    // Only on branches that switched damages on (branches.json `features.damages`).
    if (features?.damages) {
      baseItems.push({
        label: 'Damages',
        path: '/damages',
        icon: PackageX,
        description: 'Damaged stock and warehouse shipments'
      });
    }

    return baseItems;
  };

  const navigationItems = getNavigationItems();

  const handleNavigation = (path) => {
    // A page with unsaved changes (e.g. the report builder) can veto navigation.
    const guard = useAppStore.getState().leaveGuard;
    if (guard && !guard()) return;
    navigate(path);
    onClose();
  };

  // Focus first button when modal opens and handle keyboard navigation
  useEffect(() => {
    if (isOpen && firstButtonRef.current) {
      setTimeout(() => {
        firstButtonRef.current.focus();
      }, 50); // Reduced delay for faster focus
    }
  }, [isOpen]);

  // Handle keyboard navigation with number keys, function keys, and Escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;
      
      // Handle Escape key to close modal
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      
      const key = event.key;
      let targetIndex = -1;
      
      // Handle number keys 1-9 for first 9 items
      const number = parseInt(key);
      if (number >= 1 && number <= 9 && number <= navigationItems.length) {
        targetIndex = number - 1;
      }
      
      // Handle function keys F1-F12 for items 10 and above
      if (key.startsWith('F')) {
        const functionKeyNumber = parseInt(key.substring(1));
        if (functionKeyNumber >= 1 && functionKeyNumber <= 12) {
          // F1 = item 10, F2 = item 11, etc.
          const itemIndex = 9 + functionKeyNumber - 1;
          if (itemIndex < navigationItems.length) {
            targetIndex = itemIndex;
          }
        }
      }
      
      if (targetIndex >= 0 && targetIndex < navigationItems.length) {
        event.preventDefault();
        
        // Highlight the selected item briefly
        setHighlightedIndex(targetIndex);
        setTimeout(() => setHighlightedIndex(-1), 200);
        
        // Navigate after a short delay for visual feedback
        setTimeout(() => {
          const targetItem = navigationItems[targetIndex];
          if (targetItem) {
            handleNavigation(targetItem.path);
          }
        }, 150);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, navigationItems, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Navigation"
      size="xl"
      showCloseButton={true}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {navigationItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          const isFirst = index === 0;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              ref={isFirst ? firstButtonRef : null}
              onClick={(e) => {
                // Let ⌘/Ctrl/Shift/Alt+click (and middle-click, which fires no
                // onClick) fall through to the browser so the route opens in a
                // new tab/window via the real href. A plain click still does
                // in-app navigation and closes the modal, as before.
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                handleNavigation(item.path);
              }}
              className={`group relative flex flex-col items-center justify-center p-6 rounded-xl font-medium transition-all duration-200 text-center no-underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[140px] ${
                isActive
                  ? 'bg-blue-50 text-blue-600 border-2 border-blue-200 shadow-lg'
                  : highlightedIndex === index
                  ? 'bg-yellow-50 text-yellow-700 border-2 border-yellow-200 shadow-lg'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}
            >
              {/* Shortcut key indicator */}
              <div className="absolute top-3 left-3 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600 group-hover:bg-gray-200 transition-colors">
                {index < 9 ? index + 1 : `F${index - 8}`}
              </div>
              
              {/* Active indicator */}
              {isActive && (
                <div className="absolute top-3 right-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                </div>
              )}
              
              {/* Icon */}
              <div className="mb-3">
                {item.path === '/' ? (
                  <div className="relative">
                    <Icon className="h-8 w-8" />
                    {itemCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {itemCount}
                      </span>
                    )}
                  </div>
                ) : (
                  <Icon className="h-8 w-8" />
                )}
              </div>
              
              {/* Content */}
              <div className="space-y-1">
                <div className="font-semibold text-base">{item.label}</div>
                <div className="text-sm text-gray-500 group-hover:text-gray-600 transition-colors">{item.description}</div>
              </div>
            </Link>
          );
        })}
      </div>
      
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="text-sm text-gray-500 text-center space-y-1">
          <div>
            Press <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Ctrl + M</kbd> to open this navigation from anywhere
          </div>
          <div>
            {navigationItems.length <= 9 ? (
              <div>
                Press <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">1-{navigationItems.length}</kbd> to navigate quickly
              </div>
            ) : (
              <div className="space-y-1">
                <div>
                  Press <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">1-9</kbd> for first 9 items
                </div>
                <div>
                  Press <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">F1-F{Math.min(12, navigationItems.length - 9)}</kbd> for items 10+
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NavigationModal;
